import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import {
  type InvitePreview,
  type LoginResponse,
  passwordSchema,
  ROLE_LABELS,
} from '@brimostech/shared';
import { Button, EmptyState, Input, Label, Skeleton, Spinner, toast } from '@brimostech/ui';
import { AuthLayout } from '@/features/auth/login-page';
import { useAuth } from '@/features/auth/auth-context';
import { api } from '@/lib/api-client';
import { errorMessage } from '@/lib/api-errors';

const lookupSchema = z.object({
  destination: z.string().trim().min(3, 'Informe o e-mail ou celular do convite'),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Código de 6 dígitos'),
});
const acceptSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, 'Informe seu nome completo')
      .max(160)
      .optional()
      .or(z.literal('')),
    password: passwordSchema,
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: 'As senhas não conferem',
    path: ['confirm'],
  });

/** Public page: the invited person previews the invite and creates (or links) the account. */
export function InvitePage() {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [lookup, setLookup] = useState<{ code: string; destination: string } | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const lookupForm = useForm<z.infer<typeof lookupSchema>>({
    resolver: zodResolver(lookupSchema),
    defaultValues: { destination: '', code: '' },
  });
  const acceptForm = useForm<z.infer<typeof acceptSchema>>({
    resolver: zodResolver(acceptSchema),
    defaultValues: { name: '', password: '', confirm: '' },
  });

  const preview = useQuery({
    queryKey: ['invite-preview', token ?? lookup],
    queryFn: () =>
      token
        ? api<InvitePreview>(`/invites/public/${token}`, { auth: false })
        : api<InvitePreview>('/invites/public/lookup', {
            method: 'POST',
            body: lookup,
            auth: false,
          }),
    enabled: !!token || !!lookup,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (values: z.infer<typeof acceptSchema>) =>
      api<LoginResponse>('/invites/public/accept', {
        method: 'POST',
        auth: false,
        body: {
          ...(token ? { token } : lookup),
          password: values.password,
          name: values.name || undefined,
        },
      }),
    onSuccess: (result) => {
      adoptSession(result);
      toast.success('Conta pronta! Bem-vindo(a).');
      navigate('/', { replace: true });
    },
    onError: (error) => setServerError(errorMessage(error)),
  });

  if (!token && !lookup) {
    return (
      <AuthLayout>
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Ativar convite</h1>
          <p className="text-sm text-muted-foreground">
            Informe o e-mail ou celular que recebeu o convite e o código de 6 dígitos.
          </p>
        </div>
        <form
          onSubmit={lookupForm.handleSubmit((v) => setLookup(v))}
          className="space-y-5"
          noValidate
        >
          <div className="space-y-2">
            <Label htmlFor="destination">E-mail ou celular</Label>
            <Input
              id="destination"
              autoComplete="username"
              aria-invalid={!!lookupForm.formState.errors.destination}
              {...lookupForm.register('destination')}
            />
            {lookupForm.formState.errors.destination ? (
              <p className="text-sm text-destructive">
                {lookupForm.formState.errors.destination.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="code">Código do convite</Label>
            <Input
              id="code"
              inputMode="numeric"
              placeholder="000000"
              className="text-center text-lg tracking-[0.4em]"
              aria-invalid={!!lookupForm.formState.errors.code}
              {...lookupForm.register('code')}
            />
            {lookupForm.formState.errors.code ? (
              <p className="text-sm text-destructive">{lookupForm.formState.errors.code.message}</p>
            ) : null}
          </div>
          <Button type="submit" className="w-full">
            Continuar
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">Já tenho conta</Link>
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (preview.isPending) {
    return (
      <AuthLayout>
        <div className="space-y-3">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </AuthLayout>
    );
  }

  if (preview.isError) {
    return (
      <AuthLayout>
        <EmptyState
          icon={<Building2 />}
          title="Convite inválido ou expirado"
          description={errorMessage(preview.error)}
          action={
            <Button asChild variant="outline">
              <Link to="/convite" onClick={() => setLookup(null)}>
                Tentar com o código
              </Link>
            </Button>
          }
        />
      </AuthLayout>
    );
  }

  const data = preview.data;
  const { errors, isSubmitting } = acceptForm.formState;
  return (
    <AuthLayout>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Olá, {data.personName.split(' ')[0]}!
        </h1>
        <p className="text-sm text-muted-foreground">
          Você foi convidado(a) como <strong>{ROLE_LABELS[data.role]}</strong> no{' '}
          <strong>{data.condominiumName}</strong>
          {data.unitLabel ? ` (${data.unitLabel})` : ''}, administrado por {data.organizationName}.
        </p>
      </div>
      <form
        onSubmit={acceptForm.handleSubmit((v) => accept.mutate(v))}
        className="space-y-5"
        noValidate
      >
        <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
          Conta: <strong>{data.destinationMasked}</strong>
          {data.accountExists ? (
            <p className="text-muted-foreground">
              Já existe uma conta para este contato. Informe a senha dela para vincular o convite.
            </p>
          ) : null}
        </div>
        {!data.accountExists ? (
          <div className="space-y-2">
            <Label htmlFor="name">Seu nome completo</Label>
            <Input
              id="name"
              autoComplete="name"
              defaultValue={data.personName}
              aria-invalid={!!errors.name}
              {...acceptForm.register('name')}
            />
            {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="password">
            {data.accountExists ? 'Senha da sua conta' : 'Crie uma senha'}
          </Label>
          <Input
            id="password"
            type="password"
            autoComplete={data.accountExists ? 'current-password' : 'new-password'}
            aria-invalid={!!errors.password}
            {...acceptForm.register('password')}
          />
          {errors.password ? (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          ) : !data.accountExists ? (
            <p className="text-xs text-muted-foreground">
              Pelo menos 8 caracteres, com letras e números.
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirme a senha</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirm}
            {...acceptForm.register('confirm')}
          />
          {errors.confirm ? (
            <p className="text-sm text-destructive">{errors.confirm.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {serverError}
          </div>
        ) : null}
        <Button type="submit" className="w-full" disabled={isSubmitting || accept.isPending}>
          {accept.isPending ? <Spinner /> : null}
          {data.accountExists ? 'Vincular e entrar' : 'Criar conta e entrar'}
        </Button>
      </form>
    </AuthLayout>
  );
}
